import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { IContract } from '@models/contract.class';
import {
  PriceAlert,
  PRICE_ALERT_OPERATOR,
  PRICE_ALERT_TYPE,
} from '@models/price-alerts.class';
import { SearchBoxIssueItem } from '@models/search-box-issue-item';
import { Store } from '@ngrx/store';
import { of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';
import {
  AppState,
  NO_SCROLL,
  OPEN_PRICE_ALERTS,
  TRACKING_EVENTS,
} from 'src/app';
import { MixpanelService, NotificationService, SearchService } from 'src/core';
import { PriceAlertsService } from 'src/core/services/price-alerts.service';
import {
  getMarketName,
  MarketItem,
  MarketType,
  SharedSolaceStreamService,
} from 'src/solace';
import { SideModalComponent } from '../side-modal/side-modal.component';

@Component({
  selector: 'hb-price-alerts',
  templateUrl: './price-alerts.component.html',
  styleUrls: ['./price-alerts.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PriceAlertsComponent implements OnInit, OnDestroy {
  @ViewChild('sideModal') sideModal: SideModalComponent;

  public alertsByIssue: Array<{ key: string; value: PriceAlert[] }>;

  public isAddingAlert: boolean;
  public isEditingAlert: boolean;
  public isDeletingAlert: boolean;

  public alert: PriceAlert = {} as PriceAlert;

  public alertTriggers = [
    {
      id: PRICE_ALERT_OPERATOR.equalOrLessThan,
      name: 'El precio sea igual o menor a',
    },
    {
      id: PRICE_ALERT_OPERATOR.equalOrGreaterThan,
      name: 'El precio sea igual o mayor a',
    },
    { id: PRICE_ALERT_OPERATOR.both, name: 'El precio suba o baje' },
  ];

  public selectedAlertTrigger: { id: PRICE_ALERT_OPERATOR; name: string } =
    this.alertTriggers[0];

  public triggerUnits: PRICE_ALERT_TYPE;

  public isConfirmationModalOpen: boolean;
  public confirmationModalHandler: () => void;

  /**
   * Displays basic issue data in the header of an existing alert
   *
   * It displays the name, symbol, and price activity.
   */
  public issueData: Partial<SearchBoxIssueItem> & {
    openPrice: number;
    variationPercentage: number;
    lastPrice: number;
  } = {} as any;

  public priceValidationErrors?: Partial<{
    isError: boolean;
    isNotice: boolean;
    isSameContract: boolean;
    isSamePrice: boolean;
  }>;

  /**
   * Alerts between all contracts
   *
   * They don't contain the full alert information. Only its id, and
   * price/percentage threshold.
   *
   * These are used to check if an alert can be created, or if it should
   * be updated.
   */
  private existingAlertsByIssue: Record<string, IssueExistingAlerts>;

  private contractId: string;

  private destroyed$: Subject<void> = new Subject();
  private selectedIssue: SearchBoxIssueItem;

  private solaceSubscription: Subscription = Subscription.EMPTY;

  /**
   * TODO: Convert this into a common constant for the whole app that anyone can
   * subscribe to to avoid repeating the same pipes in multiple components
   */
  private readonly currentContract$ = this.store.select('currentContract').pipe(
    takeUntil(this.destroyed$),
    map((e: any) => e.toJS()),
    distinctUntilChanged((prev, last) => prev.contractID === last.contractID),
  );
  /* -------------------------------------------------------------------------- */

  constructor(
    private readonly store: Store<AppState>,
    private readonly priceAlertsService: PriceAlertsService,
    private readonly sharedSolaceStreamService: SharedSolaceStreamService,
    private readonly notificationService: NotificationService,
    private readonly searchService: SearchService,
    private readonly mixpanelService: MixpanelService,
  ) {}

  ngOnInit() {
    this.sideModal.showModal();
    this.mixpanelService.track(TRACKING_EVENTS.price_alert_view.event);

    this.updateScroll(true);

    this.currentContract$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ contractID }: IContract) => {
        this.contractId = contractID;
        this.priceAlertsService.initializeRoute(contractID);

        this.priceAlertsService
          .getAllAlerts()
          .pipe(
            catchError(() => {
              this.notificationService.showError(NOTIFICATIONS.get.error);
              return of([]);
            }),
          )
          .subscribe((alerts: PriceAlert[]) => {
            this.mixpanelService.addUserProps({ $price_alerts: alerts.length });
            this.separateAlertsByContract(alerts, contractID);
          });
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.sideModal.handleHide();
    this.updateScroll(false);
  }

  /* -------------------------------------------------------------------------- */
  /*                                  Handlers                                  */
  /* -------------------------------------------------------------------------- */

  public handleNewAlertClick() {
    this.isAddingAlert = true;

    // Resets values that could be kept in memory before creating a new alert
    this.alert = {} as PriceAlert;
    this.selectedAlertTrigger = this.alertTriggers[0];
  }

  public handleToggleAlertClick(alert: PriceAlert) {
    this.toggleAlert(alert);
  }

  public handleDeleteAlertClick(alertId: string, issueId: string) {
    this.isConfirmationModalOpen = true;
    this.isDeletingAlert = true;

    this.confirmationModalHandler = () => {
      this.deleteAlert(alertId, issueId);
    };
  }

  /**
   * When the user changes when they want to be alerted
   */
  public handleAlertTypeChange(operator: PRICE_ALERT_OPERATOR) {
    /**
     * TODO: Remove triggerUnits and use alert.type
     */
    this.triggerUnits =
      operator === PRICE_ALERT_OPERATOR.both
        ? PRICE_ALERT_TYPE.percentage
        : PRICE_ALERT_TYPE.price;

    this.alert.operator = operator;
    this.alert.type = this.triggerUnits;
  }

  public handleSearchBoxIssueSelection(issue: SearchBoxIssueItem) {
    this.selectedIssue = issue;
    this.obtainIssueDetailsFromSolace(issue, true);
  }

  public handleModalGoBack() {
    if (!this.isAddingAlert && !this.isEditingAlert) {
      this.sideModal.handleHide();
      this.store.dispatch({ type: OPEN_PRICE_ALERTS, payload: false });
      return;
    }

    this.priceValidationErrors = undefined;

    this.isAddingAlert = false;
    this.isEditingAlert = false;

    // Unsubscribe to the previous topic if it's still active.
    this.solaceSubscription.unsubscribe();
  }

  /**
   * Loads the details of an alert and allows it to be edited
   * @param alert that was clicked and will be showed in detail
   */
  public handleAlertClick(alert: PriceAlert): void {
    this.isEditingAlert = true;

    this.alert = alert;

    const issue = (this.searchService.issues as SearchBoxIssueItem[]).find(
      (e) => e.issueID === alert.issue_id,
    );

    if (!issue) {
      /**
       * TODO: Implement a better handler for this scenario
       *
       * Ideally, this scenario doesn't happen. This could only occur when:
       *  - The endpoint with the SIC/BMV information failed, or resolved after
       *    requesting the alert details.
       *  - The alert had an invalid issue_id.
       *  - The alert was created for an issue_id that stopped being in the market.
       */
      return;
    }

    this.selectedAlertTrigger = this.getAlertTrigger(alert.operator);

    this.obtainIssueDetailsFromSolace(issue);
  }

  public handleLimitPriceChange(price: string | number) {
    const priceAsNumber = Number(price);

    this.alert.limit_price = priceAsNumber;

    const issueId = this.isEditingAlert
      ? this.alert.issue_id
      : this.selectedIssue.issueID;

    this.validateDuplicatedAlert(issueId, priceAsNumber);
  }

  public handleLimitPercentageChange(percentage: string | number) {
    const percentageAsNumber = Number(percentage);

    this.alert.limit_percentage = percentageAsNumber;
  }

  /**
   * Updates an existing alert
   */
  public handleUpdateAlertClick() {
    // There's no need to assign different properties, since this.alert
    // keeps getting updated when the alert type or triggers change.
    const alert: PriceAlert = {
      ...(this.alert as PriceAlert),
      // Updated alerts always are activated by default
      is_active: true,
    };

    this.updateAlert(alert);
  }

  /**
   * Handler for the create alert button
   */
  public handleCreateAlertClick() {
    /**
     * TODO: Remove this hardcode once alerts can trigger real-time notifications
     */
    const isEmailNotification = true;

    // There's no need to assign different properties, since this.alert
    // keeps getting updated when the alert type or triggers change.
    const alert: PriceAlert = {
      ...this.alert,
      issue_id: this.selectedIssue.issueID,
      is_sic: this.selectedIssue.instrumentType === MarketType.SIC,
      is_email_notification: isEmailNotification,
    };

    /**
     * When the issueID of the alert doesn't exist yet, the alert can be created
     */
    if (
      !this.existingAlertsByIssue.hasOwnProperty(this.selectedIssue.issueID)
    ) {
      this.createAlert(alert);
      return;
    }

    if (
      alert.type === PRICE_ALERT_TYPE.price &&
      !this.existingAlertsByIssue[this.selectedIssue.issueID].price.some(
        (price) => price.value === alert.limit_price,
      )
    ) {
      this.createAlert(alert);
    }

    if (
      alert.type === PRICE_ALERT_TYPE.percentage &&
      this.existingAlertsByIssue[this.selectedIssue.issueID].percentage.some(
        (percentage) => percentage.value === alert.limit_percentage,
      )
    ) {
      this.isConfirmationModalOpen = true;

      this.confirmationModalHandler = () => {
        (this.alert as PriceAlert).id = this.existingAlertsByIssue[
          this.selectedIssue.issueID
        ].percentage.find(
          (percentage) => percentage.value === alert.limit_percentage,
        ).id;

        this.alert.issue_id = alert.issue_id;
        this.updateAlert({ ...this.alert, is_active: true });
      };
    }
  }

  /**
   * Obtain the corresponding item in the alertTriggers array for a trigger operator
   *
   * @param operator that will trigger the alert
   * @returns the item containing an id and name
   */
  public getAlertTrigger(operator: PRICE_ALERT_OPERATOR): {
    id: PRICE_ALERT_OPERATOR;
    name: string;
  } {
    return this.alertTriggers.find((trigger) => trigger.id === operator);
  }

  /* -------------------------------------------------------------------------- */
  /*                               Data wrangling                               */
  /* -------------------------------------------------------------------------- */

  private separateAlertsByContract(alerts: PriceAlert[], contractID: string) {
    const alertsInCurrentContract = alerts.filter(
      (alert) => alert.account_id === contractID,
    );

    this.alertsByIssue = this.groupAlertsByIssue(alertsInCurrentContract);

    this.existingAlertsByIssue =
      this.createAlertsExistingPricesAndPercentages(alerts);
  }

  /**
   * Convert a list of alerts into an ordered array of {key, val} elements
   *
   * This will take a list of alerts, and create another list using the issueId
   * as a pivot to group them.
   *
   * @param alerts Price alerts to be grouped
   *
   * @example
   *
   * // Some alert properties were not included for brevity and clarity
   *
   * let alerts = [
   *   { issueId: 'GOOGL *', id: 1 },
   *   { issueId: 'APPL *', id: 2 },
   *   { issueId: 'AMZN *', id: 3 },
   *   { issueId: 'APPL *', id: 4 },
   * ];
   *
   * this.groupAlertsByIssue(alerts);
   * // =>
   *  [
   *    { key: 'APPL *', value: [
   *      { issueId: 'APPL *', id: 2 },
   *      { issueId: 'APPL *', id: 4 },
   *    ]},
   *    { key: 'AMZN *', value: [{ issueId: 'AMZN *', id: 3 }] },
   *    { key: 'GOOGL *', value: [{ issueId: 'GOOGL *', id: 1 }] },
   *  ]
   */
  private groupAlertsByIssue(
    alerts: PriceAlert[],
  ): Array<{ key: string; value: PriceAlert[] }> {
    return this.createKeyValueArray(
      this.createAlertsDictionaryByIssue(alerts),
    ).sort((a, b) => a.key.localeCompare(b.key));
  }

  /**
   * Creates a { key, value } array from an object
   * @param obj any object with own properties
   *
   * @example
   * let obj = { a: 1, b: 2, c: 3 };
   *
   * this.createKeyValueArray(obj);
   * // =>
   *   [
   *     { key: 'a', value: 1 },
   *     { key: 'b', value: 2 },
   *     { key: 'c', value: 3 },
   *   ]
   */
  private createKeyValueArray<T>(
    obj: Record<string, T>,
  ): Array<{ key: string; value: T }> {
    return Object.keys(obj).map((key) => ({ key, value: obj[key] }));
  }

  /**
   * Convert a list of alerts into a dictionary of issues with the list of their alerts
   * @param alerts list of alerts with issue_id
   * @returns an object with the issue_id as keys, and the original items as values
   *
   * @example
   * // Some alert properties were not included for brevity and clarity
   *
   * let alerts = [
   *   { issueId: 'GOOGL *', id: 1 },
   *   { issueId: 'APPL *', id: 2 },
   *   { issueId: 'AMZN *', id: 3 },
   *   { issueId: 'APPL *', id: 4 },
   * ];
   *
   * this.createAlertsDictionaryByIssue(alerts);
   * // =>
   *  {
   *    'GOOGL *': [{ issueId: 'GOOGL *', id: 1 }]},
   *    'APPL *': [{ issueId: 'APPL *', id: 2 }, { issueId: 'APPL *', id: 4 }]},
   *    'AMZN *': [ { issueId: 'AMZN *', id: 3 }]},
   *  }
   */
  private createAlertsDictionaryByIssue(
    alerts: PriceAlert[],
  ): Record<string, PriceAlert[]> {
    return alerts.reduce(
      (dict, alert) => ({
        ...dict,
        [alert.issue_id]: [...(dict[alert.issue_id] || []), alert],
      }),
      {} as Record<string, PriceAlert[]>,
    );
  }

  /**
   * Convert a list of alerts into a dictionary of issues with the list of their alerts
   * @param alerts list of alerts with issue_id
   * @returns an object with the issue_id as keys, and the original items as values
   */
  private createAlertsExistingPricesAndPercentages(
    alerts: PriceAlert[],
  ): Record<string, IssueExistingAlerts> {
    const existingAlerts: Record<string, IssueExistingAlerts> = {};

    for (const alert of alerts) {
      const id = alert.id;
      const isSameContract = alert.account_id === this.contractId;

      if (existingAlerts.hasOwnProperty(alert.issue_id)) {
        alert.type === PRICE_ALERT_TYPE.price &&
          existingAlerts[alert.issue_id].price.push({
            value: alert.limit_price,
            isSameContract,
            id,
          });
        alert.type === PRICE_ALERT_TYPE.percentage &&
          existingAlerts[alert.issue_id].percentage.push({
            value: alert.limit_percentage,
            id,
          });
      } else {
        existingAlerts[alert.issue_id] = {
          price:
            alert.type === PRICE_ALERT_TYPE.price
              ? [{ value: alert.limit_price, id, isSameContract }]
              : [],
          percentage:
            alert.type === PRICE_ALERT_TYPE.percentage
              ? [{ value: alert.limit_percentage, id }]
              : [],
        };
      }
    }

    return existingAlerts;
  }

  /* -------------------------------------------------------------------------- */
  /*                                  Utilities                                 */
  /* -------------------------------------------------------------------------- */

  private validateDuplicatedAlert(issueId: string, price: number) {
    // When no alert for this issue exists, can't be  duplicate
    if (!this.existingAlertsByIssue.hasOwnProperty(issueId)) {
      this.priceValidationErrors = undefined;
      return;
    }

    const duplicatedAlert = this.existingAlertsByIssue[issueId].price.find(
      (alertPrice) => alertPrice.value === price,
    );

    // No alert could be found in the existing alerts, so it's not a duplicate
    if (!duplicatedAlert) {
      this.priceValidationErrors = undefined;
      return;
    }

    // An alert is being edited, and it was found as "duplicated".
    // It shouldn't be marked as such, because it's the same
    if (this.isEditingAlert && duplicatedAlert.id === this.alert.id) {
      this.priceValidationErrors = undefined;
      return;
    }

    this.priceValidationErrors = {
      isError: true,
      isSameContract: duplicatedAlert.isSameContract,
    };
  }

  /**
   * Closes the PriceAlerts modal, redirecting to the original route and screen
   */
  private redirectToPreviousScreen() {
    this.isAddingAlert = false;
    this.isEditingAlert = false;

    this.handleModalGoBack();
  }

  /**
   * This will obtain the details of an issue using Solace
   * @param issue Issue to get the info of. Contains at least instrumentType and issueId
   */
  private obtainIssueDetailsFromSolace(
    issue: SearchBoxIssueItem,
    overrideCurrentAlert?: boolean,
  ) {
    const marketObservable$ =
      this.sharedSolaceStreamService.getObservableSymbol<MarketItem>(
        getMarketName(issue.instrumentType),
        issue.issueID,
        'alerts',
      );

    // Unsubscribe to the previous topic if it's still active.
    this.solaceSubscription.unsubscribe();
    this.solaceSubscription = marketObservable$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((symbol) => {
        if (!symbol || !symbol.hechos || !symbol.hechos.body) {
          return;
        }

        const { lastPrice, variationPercentage, openPrice } =
          symbol.hechos.body;

        this.issueData = {
          ...issue,
          openPrice,
          variationPercentage,
          lastPrice,
        };

        if (overrideCurrentAlert) {
          this.alert = {
            ...this.alert,
            last_price: lastPrice,
            limit_price: lastPrice,
          };
        }
      });
  }

  /**
   * Requests to block/unblock the body scroll
   * @param block blocks when true, and unblocks when false
   */
  private updateScroll(block: boolean) {
    this.store.dispatch({
      type: NO_SCROLL,
      payload: { block, source: 'PriceAlertsComponent' },
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                    Methods that consume the CRUD service                   */
  /* -------------------------------------------------------------------------- */

  private createAlert(alert: PriceAlert) {
    this.priceAlertsService.createAlert(alert).subscribe({
      next: () => {
        this.mixpanelService.track(
          TRACKING_EVENTS.price_alert_create.event,
          TRACKING_EVENTS.price_alert_create.success(
            alert.type,
            alert.issue_id,
          ),
        );
        this.notificationService.showInfo(
          NOTIFICATIONS.create.success(alert.issue_id),
        );
        this.redirectToPreviousScreen();
      },
      error: (error) => {
        this.mixpanelService.track(
          TRACKING_EVENTS.price_alert_create.event,
          TRACKING_EVENTS.price_alert_create.error(
            alert.type,
            alert.issue_id,
            error,
          ),
        );
        this.notificationService.showError(
          NOTIFICATIONS.create.error(alert.issue_id),
        );
      },
    });
  }

  private updateAlert(alert: PriceAlert) {
    this.priceAlertsService.editAlert(alert.id, alert).subscribe({
      next: () => {
        this.mixpanelService.track(
          TRACKING_EVENTS.price_alert_update.event,
          TRACKING_EVENTS.price_alert_update.success(alert.type),
        );
        this.notificationService.showInfo(
          NOTIFICATIONS.update.success(alert.issue_id),
        );
        this.isConfirmationModalOpen = false;
        this.redirectToPreviousScreen();
      },
      error: (error) => {
        this.mixpanelService.track(
          TRACKING_EVENTS.price_alert_update.event,
          TRACKING_EVENTS.price_alert_update.error(error, alert.type),
        );
        this.notificationService.showError(
          NOTIFICATIONS.update.error(alert.issue_id),
        );
        this.isConfirmationModalOpen = false;
      },
    });
  }

  private toggleAlert(alert: PriceAlert) {
    alert.is_active = !alert.is_active;

    this.priceAlertsService
      .editAlert(alert.id, { ...alert, is_active: alert.is_active })
      .subscribe({
        next: () => {
          this.notificationService.showInfo(
            NOTIFICATIONS.toggle.success(alert.issue_id, alert.is_active),
          );
        },
        error: () => {
          this.notificationService.showError(
            NOTIFICATIONS.toggle.error(alert.issue_id, alert.is_active),
          );

          // Undo the changes to keep the dom in sync
          alert.is_active = !alert.is_active;
        },
      });
  }

  private deleteAlert(alertId: string, issueId: string) {
    this.priceAlertsService.deleteAlert(alertId).subscribe({
      next: () => {
        this.mixpanelService.track(
          TRACKING_EVENTS.price_alert_delete.event,
          TRACKING_EVENTS.price_alert_delete.payload(issueId),
        );
        this.notificationService.showInfo(
          NOTIFICATIONS.delete.success(this.alert.issue_id),
        );
        this.redirectToPreviousScreen();
      },
      error: () => {
        this.notificationService.showError(
          NOTIFICATIONS.delete.error(this.alert.issue_id),
        );
      },
    });

    this.isConfirmationModalOpen = false;
    this.isEditingAlert = false;
    this.isAddingAlert = false;
  }
}

/**
 * Smaller way to store existing alerts without copying the wholea lert
 */
interface IssueExistingAlerts {
  price: Array<{ value: number; id: string; isSameContract: boolean }>;
  percentage: Array<{ value: number; id: string }>;
}

const NOTIFICATIONS = {
  get: {
    error: {
      message: `Ocurrió un error al obtener tus alertas de precio existentes.`,
    },
  },
  create: {
    success: (symbol: string) => ({
      message: `Se ha creado una alerta de precio para ${symbol}`,
    }),
    error: (symbol: string) => ({
      message: `No se ha podido crear la alerta de precio para ${symbol}`,
    }),
  },
  update: {
    success: (symbol: string) => ({
      message: `Se ha actualizado la alerta de precio para ${symbol}`,
    }),
    error: (symbol: string) => ({
      message: `No se ha podido actualizar la alerta de precio para ${symbol}`,
    }),
  },
  delete: {
    success: (symbol: string) => ({
      message: `Se ha eliminado la alerta de precio para ${symbol}`,
    }),
    error: (symbol: string) => ({
      message: `No se ha podido eliminar la alerta de precio para ${symbol}`,
    }),
  },
  toggle: {
    success: (symbol: string, activated: boolean) => ({
      message: `Se ha ${
        activated ? 'activado' : 'desactivado'
      } la alerta de precio para ${symbol}`,
    }),
    error: (symbol: string, activated: boolean) => ({
      message: `La alerta de precio para ${symbol} no pudo ser ${
        activated ? 'activada' : 'desactivada'
      }.`,
    }),
  },
};
